import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { type ColumnDef } from '@tanstack/react-table'
import DataTable from '../../components/DataTable'
import Button from '../components/Button'
import { getStudents } from '../../services/students'
import { Student, School } from '../../types'
import { useSchoolStore } from '../../stores/schoolsStore'

const StudentsList: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { schools, fetchSchools } = useSchoolStore()

  useEffect(() => {
    fetchStudents()
    fetchSchools()
  }, [])

  const fetchStudents = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getStudents()
      setStudents(response.data)
    } catch (err: any) {
      console.error('Error fetching students:', err)
      setError(err.response?.data?.error || 'Failed to fetch students')
    } finally {
      setLoading(false)
    }
  }

  const handleViewGoals = (studentId: string) => {
    navigate(`/goals/students/${studentId}`)
  }

  const getSchoolName = (schoolId: string) => {
    const school = schools.find((school: School) => school._id === schoolId)
    return school?.displayName || 'Unknown School'
  }

  const columns = useMemo<ColumnDef<Student>[]>(
    () => [
      {
        accessorKey: 'studentId',
        header: 'ID',
        cell: (info) => {
          const value = info.getValue() as string | undefined
          return <div className="text-sm text-gray-900">{value || '-'}</div>
        },
      },
      {
        accessorKey: 'firstName',
        header: 'First Name',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: 'lastName',
        header: 'Last Name',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: 'school',
        header: 'School',
        cell: (info) => {
          const school = info.getValue() as any
          const schoolId = typeof school === 'object' ? school._id : school
          return (
            <div className="text-sm text-gray-900">
              {getSchoolName(schoolId)}
            </div>
          )
        },
      },
      {
        accessorKey: 'grade',
        header: 'Grade',
        cell: (info) => (
          <div className="text-sm text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <Button
            onClick={(e) => {
              e.stopPropagation()
              handleViewGoals(info.row.original._id)
            }}
            variant="link"
            className="mr-3"
            title="View student goals"
          >
            <span>View</span>
          </Button>
        ),
      },
    ],
    [navigate, schools],
  )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Student Goals</h1>
        <p className="mt-2 text-gray-600">
          Select a student to view and manage their goals
        </p>
      </div>

      <DataTable
        data={students}
        columns={columns}
        searchPlaceholder="Search students..."
        emptyStateTitle="No students found"
        emptyStateDescription="You don't have access to any students yet."
        loading={loading}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}
    </div>
  )
}

export default StudentsList
